import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Volume2, VolumeX, Trash2, CheckCircle2, Loader2 } from "lucide-react";
import { PendingCard, confirmAll } from "@/components/PendingCard";
import { API, http, emitRefresh, refreshSession } from "@/lib/api";
import { ArcReactor } from "@/components/ArcReactor";
import { useRecorder, speak, stopSpeaking, micError } from "@/hooks/useVoice";
import { useWakeWord, wakeSupported } from "@/hooks/useWakeWord";
import { Ear, EarOff } from "lucide-react";

const SESSION_KEY = "jarvis_session";
const sessionId = localStorage.getItem(SESSION_KEY) || crypto.randomUUID();
localStorage.setItem(SESSION_KEY, sessionId);

const strip = (t) => t.replace(/<(action|omvexis)>[\s\S]*?(<\/(action|omvexis)>|$)/g, "").replace(/\*\*/g, "").replace(/\n{3,}/g, "\n\n").trim();

const SUGGESTIONS = [
  "Give me today's Omvexis status: overdue, awaiting contract and paid",
  "Which buyers owe the most right now?",
  "Show my unpaid Omvexis shipments and who owes the most",
  "Send payment-due notices for all overdue Omvexis shipments",
];

const APPROVE_RE = /^\s*(yes[,\s]+)?(confirm|approve|go ahead)( (them|all|everything|it))?\s*[.!]*\s*$/i;
const CANCEL_RE = /^\s*(cancel|reject|discard|don'?t send)( (them|all|everything|it))?\s*[.!]*\s*$/i;

async function voiceApproval(text) {
  const kind = APPROVE_RE.test(text) ? "confirm" : CANCEL_RE.test(text) ? "cancel" : null;
  if (!kind) return null;
  const { data: items } = await http.get("/omvexis/pending");
  if (!items.length) return null;
  let ok = 0, failed = 0;
  for (const it of items) {
    const { data } = await http.post(`/omvexis/pending/${it.id}/${kind}`).catch(() => ({ data: { status: "failed" } }));
    if (data.status === "done" || data.status === "cancelled") ok += 1; else failed += 1;
  }
  emitRefresh();
  if (kind === "cancel") return `Very good, sir. I've cancelled ${ok} pending Omvexis change${ok === 1 ? "" : "s"}.`;
  return `Done, sir. ${ok} Omvexis change${ok === 1 ? "" : "s"} confirmed and sent${failed ? `; ${failed} failed — see the Omvexis page` : ""}.`;
}

async function streamChat(message, onDelta) {
  const call = () => fetch(`${API}/chat/stream`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  let res = await call();
  if (res.status === 401) { await refreshSession(); res = await call(); }
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "", final = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.type === "delta") onDelta(ev.content);
      if (ev.type === "status" || ev.type === "pending" || ev.type === "round") onDelta("", ev);
      if (ev.type === "done") final = ev;
      if (ev.type === "error") throw new Error(ev.content);
    }
  }
  return final;
}

const Line = ({ m, i, reload }) => (
  <div className={`fade-up border-l-2 pl-4 py-1 ${m.role === "user" ? "border-slate-600" : "border-cyan-400/60"}`} data-testid={`chat-message-${i}`}>
    <div className="hud-label !text-[9px] mb-1" style={{ color: m.role === "user" ? "#bba99f" : undefined }}>
      {m.role === "user" ? "> OPERATOR" : "> JARVIS"}
    </div>
    <div className={`whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "text-slate-300" : "text-cyan-100 font-mono-hud text-[13px]"} ${m.streaming ? "cursor-blink" : ""}`}>
      {strip(m.content)}
    </div>
    {m.streaming && m.status && <div className="mt-1 font-mono-hud text-[10px] uppercase tracking-wider text-cyan-400/80 flex items-center gap-1" data-testid="chat-omvexis-status"><Loader2 size={10} className="animate-spin" /> {m.status}</div>}
    {m.pending?.length > 0 && (
      <div className="mt-3 space-y-2" data-testid="chat-pending-list">
        {m.pending.map((p) => <PendingCard key={p.id + p.status} item={p} />)}
        {!m.streaming && m.pending.filter((p) => p.status === "pending").length > 1 && (
          <button className="hud-btn !py-1" onClick={async () => { await confirmAll(m.pending.filter((p) => p.status === "pending")); toast.success("All confirmed"); reload(); }} data-testid="chat-confirm-all">
            <CheckCircle2 size={12} /> Confirm all ({m.pending.filter((p) => p.status === "pending").length})
          </button>
        )}
      </div>
    )}
    {m.actions?.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-2">
        {m.actions.map((a, j) => (
          <span key={j} className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 border border-emerald-500/40 bg-emerald-950/30 rounded-full px-3 py-1 flex items-center gap-1" data-testid="chat-action-chip">
            <CheckCircle2 size={11} /> {a.type.replace("_", " ")}: {a.label}
          </span>
        ))}
      </div>
    )}
  </div>
);

export const ChatConsole = () => {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [vstate, setVstate] = useState("idle");
  const { recording, level, start, stop } = useRecorder();
  const finishing = useRef(false);
  const [wake, setWake] = useState(() => wakeSupported && localStorage.getItem("jarvis_wake") === "1");
  const end = useRef(null);

  const load = () => http.get("/chat/history", { params: { session_id: sessionId } }).then((r) => setMsgs(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (text, speakBack = voiceOn) => {
    if (!text.trim() || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", streaming: true }]);
    const patchLast = (fn) => setMsgs((m) => [...m.slice(0, -1), fn(m[m.length - 1])]);
    try {
      const approval = await voiceApproval(text);
      if (approval) {
        const { data: hist } = await http.get("/chat/history", { params: { session_id: sessionId } });
        setMsgs([...hist, { role: "user", content: text }, { role: "assistant", content: approval, actions: [{ type: "omvexis", label: "approvals processed" }] }]);
        if (speakBack) { setVstate("speaking"); await speak(approval); }
        return;
      }
      const final = await streamChat(text, (d, ev) => patchLast((l) => {
        if (!ev) return { ...l, content: l.content + d };
        if (ev.type === "status") return { ...l, status: ev.content };
        if (ev.type === "pending") return { ...l, pending: [...(l.pending || []), ev.item] };
        return { ...l, content: l.content + "\n\n" };
      }));
      patchLast(() => ({ role: "assistant", content: final.content, actions: final.actions, pending: final.pending }));
      if (final.actions?.length) { emitRefresh(); toast.success(`JARVIS executed ${final.actions.length} action(s)`); }
      if (speakBack && final.content) { setVstate("speaking"); await speak(final.content.replace(/[*#•]/g, "")); }
    } catch (e) {
      patchLast(() => ({ role: "assistant", content: `System fault: ${e.message}` }));
    } finally {
      setBusy(false);
      setVstate("idle");
    }
  };

  const onReactor = async () => {
    if (vstate === "speaking") { stopSpeaking(); return setVstate("idle"); }
    if (recording) return finishListening();
    try { await start(finishListening); setVstate("listening"); } catch (e) { toast.error(micError(e)); setVstate("idle"); }
  };

  const finishListening = async () => {
    if (finishing.current) return;
    finishing.current = true;
    setVstate("processing");
    try {
      const text = await stop();
      if (text?.trim()) await send(text, true);
      else toast("I didn't catch that, sir. Tap the reactor and speak again.");
    } catch (e) { toast.error(`Transcription failed: ${e?.response?.data?.detail || e.message}`); }
    finishing.current = false;
    setVstate("idle");
  };

  useWakeWord(wake && vstate === "idle" && !busy && !recording, async (rest, err) => {
    if (err) { toast.error("Microphone blocked for the wake word. Allow mic access and turn it on again."); return toggleWake(false); }
    if (rest && rest.split(/\s+/).length >= 2) return send(rest, true);
    try { await start(finishListening); setVstate("listening"); } catch (e) { toast.error(micError(e)); }
  });

  const toggleWake = (v = !wake) => {
    if (v && !wakeSupported) return toast.error("Wake word needs Chrome or Edge on desktop or Android.");
    localStorage.setItem("jarvis_wake", v ? "1" : "0");
    setWake(v);
    if (v) toast.success('Say "Jarvis" any time while this page is open.');
  };

  const clear = async () => { await http.delete("/chat/history", { params: { session_id: sessionId } }); setMsgs([]); };

  const status = { idle: wake ? 'SAY "JARVIS" OR TAP TO SPEAK' : "STANDBY — TAP TO SPEAK", listening: "LISTENING… STOPS WHEN YOU PAUSE", processing: "PROCESSING AUDIO…", speaking: "SPEAKING — TAP TO STOP" }[vstate];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr] gap-6 h-full">
      <div className="flex flex-col items-center justify-center gap-5 py-4 border-b xl:border-b-0 xl:border-r border-cyan-500/15">
        <ArcReactor state={vstate} onClick={onReactor} />
        {vstate === "listening" && (
          <div className="w-40 h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="voice-level-meter">
            <div className="h-full bg-cyan-400 transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} />
          </div>
        )}
        <div className="hud-label text-center" data-testid="voice-status">{status}</div>
        <button className="hud-btn hud-btn-ghost" onClick={() => setVoiceOn((v) => !v)} data-testid="toggle-voice-replies">
          {voiceOn ? <Volume2 size={14} /> : <VolumeX size={14} />} Voice replies {voiceOn ? "on" : "off"}
        </button>
        <button className={`hud-btn ${wake ? "" : "hud-btn-ghost"}`} onClick={() => toggleWake()} data-testid="toggle-wake-word">
          {wake ? <Ear size={14} /> : <EarOff size={14} />} Wake word "Jarvis" {wake ? "on" : "off"}
        </button>
      </div>
      <div className="flex flex-col min-h-[460px]">
        <div className="flex-1 overflow-y-auto hud-scroll space-y-4 pr-2 max-h-[420px]" data-testid="chat-log">
          {msgs.length === 0 && (
            <div className="space-y-3">
              <p className="font-mono-hud text-[13px] text-cyan-200/80">Good day. All systems are online. How may I assist your operations?</p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button key={s} onClick={() => send(s)} className="text-left text-sm text-slate-200 border border-white/15 rounded-full px-4 py-2 hover:border-cyan-400/60 hover:text-cyan-100 transition-colors" data-testid={`chat-suggestion-${i}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {msgs.map((m, i) => <Line key={i} m={m} i={i} reload={load} />)}
          <div ref={end} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 mt-4">
          <input className="hud-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Issue a command to JARVIS…" data-testid="chat-input" disabled={busy} />
          <button className="hud-btn" type="submit" disabled={busy || !input.trim()} data-testid="chat-send-button"><Send size={14} /> Send</button>
          <button className="hud-btn hud-btn-ghost" type="button" onClick={clear} data-testid="chat-clear-button" aria-label="Clear chat"><Trash2 size={14} /></button>
        </form>
      </div>
    </div>
  );
};
